import React, { MouseEventHandler, useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import Group from 'components/utils/Group'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TextLink from 'components/Luxkit/TextLink'
import TextButton from 'components/Luxkit/Button/TextButton'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import LineExclamationCircleIcon from 'components/Luxkit/Icons/line/LineExclamationCircleIcon'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AccommodationRoomTileDetailsVariant from './AccommodationRoomTileDetailsVariant'
import FlashHotelCheapestRateOptionVariant from './FlashHotelCheapestRateOptionVariant'
import ModalContext from 'contexts/ModalContext'
import FlashHotelRoomDetailsModal from './Modals/FlashHotelRoomDetailsModal'
import WalledContent from 'components/Common/WalledContent'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import LoyaltyRoomUpgradeBenefitLabel from 'loyalty/components/LoyaltyRoomUpgradeBenefitLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import { showFlexibleCancellation, showFreeCancellation } from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import OfferUrgencyRoomCount from 'components/Common/Labels/OfferUrgencyRoomCount'

const AccommodationPackageTileContainer = styled.div`
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
`

interface Props {
  offer: App.HotelOffer;
  packageViews: Array<OfferPackageOptionView>;
  checkIn?: string;
  detailsModalMode: 'room' | 'room-and-package';
  rooms: Array<App.Occupants> | undefined;
  availableRoomCount?: number;
  availableForPurchase?: boolean;
}

function FlashHotelRoomOptionVariant(props: Props) {
  const {
    offer,
    packageViews,
    detailsModalMode,
    checkIn,
    availableRoomCount,
    availableForPurchase,
  } = props

  const pkg = packageViews[0].package

  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])

  const cheapestPackageView = useMemo(() => packageViews.reduce((cheapest, current) => current.hotelPrice < cheapest.hotelPrice ? current : cheapest), [packageViews])

  const hasMultipleCancellationPolicies = useMemo(() => {
    const firstPackageFreeCancellation = showFreeCancellation(checkIn, false, packageViews[0].package.roomRate?.cancellationPolicy)
    const firstPackageFlexibleCancellation = showFlexibleCancellation(checkIn, false, packageViews[0].package.roomRate?.cancellationPolicy)
    const firstPackageRefundable = firstPackageFreeCancellation || firstPackageFlexibleCancellation

    return !packageViews.every(view => {
      const freeCancellation = showFreeCancellation(checkIn, false, view.package.roomRate?.cancellationPolicy)
      const flexibleCancellation = showFlexibleCancellation(checkIn, false, view.package.roomRate?.cancellationPolicy)
      const refundable = freeCancellation || flexibleCancellation

      return refundable === firstPackageRefundable
    })
  }, [packageViews, checkIn])

  const offerPageDispatch = useOfferPageDispatch()

  const handleOpenCalendar = useCallback<MouseEventHandler<HTMLAnchorElement>>(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, pkg.roomType])

  const openCapcityFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, pkg.roomType])

  const showModal = useContext(ModalContext)
  const onShowRoomDetails = useCallback(() => {
    if (pkg.roomType) {
      showModal(<FlashHotelRoomDetailsModal room={pkg.roomType} />)
    }
  }, [pkg.roomType, showModal])

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const userHasRoomUpgradesAvailable = useUserHasLoyaltyBenefit('hotel_upgrade')

  const croPopularOptionBookingRemaining = !!useOptimizelyExperiment(OptimizelyExperiments.croPopularOptionBookingRemaining, !!availableRoomCount && !!checkIn)

  return (
    <Pane border="neutral-five" borderRadius="S" data-testid="package-option">
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <AccommodationRoomTile roomPackage={pkg}>
        <AccommodationRoomTileImage
          roomPackage={pkg}
        />
        <AccommodationRoomTileDetailsVariant
          roomPackage={pkg}
          urgencyLabels={<>
            {!datesAvailable && <UrgencyLabel variant="critical2">SOLD OUT</UrgencyLabel>}
            {checkIn && !croPopularOptionBookingRemaining && <RoomsRemainingLabel roomCount={cheapestPackageView.availableRooms ?? 0} threshold="rate" />}
            {checkIn && croPopularOptionBookingRemaining && availableRoomCount && <OfferUrgencyRoomCount availableRoomCount={availableRoomCount} />}
            <BookingsCountLabel bookings={pkg.roomType.stats.booked} threshold="room" />
          </>}
          actions={availableForPurchase && datesAvailable && roomFitsCapacity && <CSSBreakpoint min="desktop">
            <TripGuard>
              <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
            </TripGuard>
          </CSSBreakpoint>}
        />
      </AccommodationRoomTile>
      <AccommodationPackageTileContainer>
        <WalledContent
          enforced={offer.walledGarden}
          wall={(
            <Group fullHeight direction="vertical" verticalAlign="end">
              <TextButton size="large" kind="secondary" onClick={onSignUpClick}>Sign up to view exclusive rates</TextButton>
            </Group>
        )}
        >
          {datesAvailable && roomFitsCapacity && (
            <VerticalSpacer gap={12}>
              {packageViews[0].hasLoyaltyTargetUpgradeAvailable && userHasRoomUpgradesAvailable && <LoyaltyRoomUpgradeBenefitLabel />}
              <FlashHotelCheapestRateOptionVariant
                key={cheapestPackageView.package.uniqueKey}
                offer={offer}
                packageView={cheapestPackageView}
                detailsModalMode={detailsModalMode}
                hasMultipleCancellationPolicies={hasMultipleCancellationPolicies}
              />
            </VerticalSpacer>
          )}
        </WalledContent>
        {(!datesAvailable || !roomFitsCapacity) && (
          <Group direction="vertical" gap={8}>
            {!roomFitsCapacity && <MessageBanner
              kind="informational-primary"
              icon={<LineExclamationCircleIcon />}
              heading="This room cannot accomodate your number of guests"
              description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={openCapcityFilter}>add more rooms</TextLink></>}
            />}
            {roomFitsCapacity && !datesAvailable && <MessageBanner
              kind="informational-primary"
              icon={<LineExclamationCircleIcon />}
              heading="This room is sold out on your dates or max number of guests exceeded"
              description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleOpenCalendar}>try different dates</TextLink></>}
            />}
            <TextButton kind="secondary" size="large" onClick={onShowRoomDetails}>
              View details
            </TextButton>
          </Group>
        )}
      </AccommodationPackageTileContainer>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  rooms: state.rooms,
  availableForPurchase: isAvailableForPurchase(state, appState),
}), FlashHotelRoomOptionVariant)
